/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */
import * as storageUtils from '@/utils/storageUtils';
import { useGetFeatureFlagsQuery, useGetSystemInfoQuery } from '@/store/apis';
import { FEATURE_FLAG } from '@/commons';
import { initializePxApi, modifyAppContextData } from '@/store/feedbackSlice';
import { useEffect } from 'react';
import { useAppDispatch } from './useAppDispatch';
import { getStoredOrBrowserLanguage } from '@/utils/langUtils';
import { actions } from '@/store/Products';

export const useFeedback = () => {
    const userInfo = storageUtils.getItem('user');
    const dispatch = useAppDispatch();
    const currentLanguage = getStoredOrBrowserLanguage();

    const featureFlagListData = useGetFeatureFlagsQuery(
        [FEATURE_FLAG.DH_QUALTRICS_FEEDBACK_ROLLOUT, FEATURE_FLAG.DH_JOULE_ROLLOUT, FEATURE_FLAG.DH_CLEAN_UP_BUTTON_ROLLOUT],
        {
            skip: !userInfo,
        }
    ).data;
    const { data: qualtricsConfigUrl } = useGetSystemInfoQuery('qualtrics.config.url', { skip: !userInfo });
    const { data: qualtricsUnitId } = useGetSystemInfoQuery('qualtrics.unit.id', { skip: !userInfo });
    const { data: qualtricsEnvironment } = useGetSystemInfoQuery('qualtrics.environment', { skip: !userInfo });

    const sub = {
        unitId: qualtricsUnitId?.value ?? '',
        environment: qualtricsEnvironment?.value ?? '',
        configUrl: qualtricsConfigUrl?.value ?? '',
    };

    const isQualtricsFeatureFlagEnabled =
        featureFlagListData?.[FEATURE_FLAG.DH_QUALTRICS_FEEDBACK_ROLLOUT]?.httpStatus === 200 &&
        featureFlagListData?.[FEATURE_FLAG.DH_QUALTRICS_FEEDBACK_ROLLOUT]?.variation !== 'false';

    const isJouleFeatureFlagEnabled =
        featureFlagListData?.[FEATURE_FLAG.DH_JOULE_ROLLOUT]?.httpStatus === 200 &&
        featureFlagListData?.[FEATURE_FLAG.DH_JOULE_ROLLOUT]?.variation === 'true';

    const isCleanUpButtonFeatureFlagEnabled =
        featureFlagListData?.[FEATURE_FLAG.DH_CLEAN_UP_BUTTON_ROLLOUT]?.httpStatus === 200 &&
        featureFlagListData?.[FEATURE_FLAG.DH_CLEAN_UP_BUTTON_ROLLOUT]?.variation === 'true';

    const requirement =
        featureFlagListData && isQualtricsFeatureFlagEnabled && userInfo && qualtricsConfigUrl && qualtricsUnitId && qualtricsEnvironment;
    let initialized = false;

    useEffect(() => {
        (async () => {
            if (!requirement) {
                return;
            }

            if (initialized) {
                return;
            }
            initialized = true;

            await dispatch(initializePxApi(sub));
            dispatch(modifyAppContextData({ languageTag: currentLanguage }));
            dispatch(actions.setIsJouleEnabled(isJouleFeatureFlagEnabled));
            dispatch(actions.setIsCleanUpButtonEnabled(isCleanUpButtonFeatureFlagEnabled));
        })();
    }, [requirement]);
};
